<template>
    <SectionBase :section="section">
        <div
            :class="['animated-text-shell', overflowClass, layoutClass, { clickable: isClickable }]"
            :style="lineStyle"
            @mouseenter="hoverActive = true"
            @mouseleave="hoverActive = false"
            @click="handleClick"
        >
            <span v-if="isClipped" class="clip-boundary left" :style="clipBoundaryStyle" />
            <span v-if="isClipped" class="clip-boundary right" :style="clipBoundaryStyle" />
            <div ref="track" class="animated-text-track">
                <div class="animated-text-sequence" v-for="repeatIndex in repeatCount" :key="repeatIndex">
                    <span
                        v-for="(token, tokenIndex) in tokens"
                        :key="`${repeatIndex}-${tokenIndex}`"
                        :class="['animated-text-token', { 'divider-icon': token.type === 'divider' }]"
                        :style="tokenStyle(tokenIndex, repeatIndex)"
                    >
                        <Icon v-if="token.type === 'divider'" :icon-name="token.icon" />
                        <template v-else>
                            <span v-for="(char, charIndex) in token.chars" :key="charIndex" class="animated-text-char">{{ char }}</span>
                        </template>
                    </span>
                </div>
            </div>
        </div>
    </SectionBase>
</template>

<script>
import gsap from "gsap";
import { mapGetters } from "vuex";
import SectionBase from "@/scripts/view/portfolio/sections/SectionBase.vue";
import Icon from "@/scripts/common/Icon.vue";

const DIVIDER_ICONS = {
    OFF: null,
    TILDE: "animated-text-divider-wave",
    ARROW: "animated-text-divider-arrow",
    DIAMOND: "animated-text-divider-sparkle",
    ASTERISK: "animated-text-divider-star",
};

// Speed values are direct marquee speeds in px/sec.
const SPEED_CHART = [
    { textSize: 21,  SLOW: 44, MEDIUM: 88, FAST: 176 },
    { textSize: 36,  SLOW: 38, MEDIUM: 76, FAST: 152 },
    { textSize: 72,  SLOW: 36, MEDIUM: 72, FAST: 144 },
    { textSize: 112, SLOW: 50, MEDIUM: 100, FAST: 200 },
];

const LINEAR_SIZE_CHART = [
    { textSize: 21, dividerSize: 24, tokenGap: 24 },
    { textSize: 36, dividerSize: 40, tokenGap: 36 },
    { textSize: 72, dividerSize: 80, tokenGap: 64 },
    { textSize: 112, dividerSize: 120, tokenGap: 80 },
];

const WAVY_SIZE_CHART = [
    { textSize: 21, dividerSize: 24, tokenGap: 24 },
    { textSize: 32, dividerSize: 36, tokenGap: 32 },
];

const WAVE_CHART = [
    { textSize: 21, waveHeight: 150, waveCycles: 1.5 },
    { textSize: 32, waveHeight: 210, waveCycles: 1.5 },
];

export default {
    name: "AnimatedText",
    components: { SectionBase, Icon },
    props: {
        section: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            hoverActive: false,
            marqueeOffsetPx: 0,
            sequenceWidth: 0,
            marqueeCycleWidth: 0,
            repeatCount: 30,
            gsapTickerCallback: null,
            setTrackX: null,
            onWindowResize: null,
            waveTokenSetters: [],
            waveTokenBaseX: [],
            waveAmplitudeCached: 0,
            waveKCached: 0,
        };
    },
    watch: {
        tokens() {
            if (this.isWavy) this.resetWaveTransforms();
            this.$nextTick(() => this.refreshSequenceWidth());
        },
        lineStyle: {
            deep: true,
            handler() {
                this.$nextTick(() => this.refreshSequenceWidth());
            },
        },
        overflowClass() {
            this.$nextTick(() => this.refreshSequenceWidth());
        },
        isWavy(val) {
            this.$nextTick(() => {
                if (val) this.cacheTokenPositions();
                else this.resetWaveTransforms();
            });
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.initializeTrackSetter();
            this.refreshSequenceWidth();
            this.startMarqueeLoop();
        });
        document.fonts.ready.then(() => {
            this.$nextTick(() => this.refreshSequenceWidth());
        });
        this.onWindowResize = () => this.refreshSequenceWidth();
        window.addEventListener("resize", this.onWindowResize);
    },
    beforeUnmount() {
        if (this.onWindowResize) {
            window.removeEventListener("resize", this.onWindowResize);
        }
        this.stopMarqueeLoop();
    },
    computed: {
        ...mapGetters("view", ["viewData"]),
        designSettings() {
            return this.section?.animatedTextDesignSettings || {};
        },
        interactionSettings() {
            return this.section?.animatedTextInteractionSettings || {};
        },
        items() {
            const values = Array.isArray(this.section?.items)
                ? this.section.items.map((item) => item?.text).filter(Boolean)
                : [];
            return values;
        },
        dividerIcon() {
            const key = this.designSettings?.dividerStyle || "OFF";
            return DIVIDER_ICONS[key] ?? null;
        },
        tokens() {
            const tokenList = [];
            this.items.forEach((item, index) => {
                if (index > 0 && this.dividerIcon) {
                    tokenList.push({ type: "divider", icon: this.dividerIcon });
                }
                tokenList.push({ type: "item", text: item, chars: item.split("") });
            });
            if (this.dividerIcon && this.items.length >= 1) {
                tokenList.push({ type: "divider", icon: this.dividerIcon });
            }
            return tokenList;
        },
        lineStyle() {
            const fontSettings = this.designSettings?.fontSettings || {};
            const textColor = this.resolveTextColor(this.designSettings?.textColor);
            const { dividerSize, tokenGap } = this.resolveDividerMetrics(fontSettings.size);
            const sequenceGap = tokenGap;
            const lineHeight = fontSettings.lineHeight || 40;
            const waveHeight = this.resolveWaveHeight(fontSettings.size);

            return {
                color: textColor,
                fontFamily: fontSettings.font || null,
                fontWeight: fontSettings.fontWeight || null,
                fontStyle: fontSettings.fontStyle || null,
                fontSize: fontSettings.size ? `${fontSettings.size}px` : null,
                lineHeight: `${lineHeight}px`,
                "--animated-text-divider-size": `${dividerSize}px`,
                "--animated-text-sequence-gap": `${sequenceGap}px`,
                "--animated-text-line-height": `${lineHeight}px`,
                "--animated-text-wave-height": `${waveHeight}px`,
            };
        },
        isWavy() {
            return (this.designSettings?.layout || "LINEAR") === "WAVY";
        },
        layoutClass() {
            return this.isWavy ? "layout-wavy" : "layout-linear";
        },
        overflowClass() {
            const overflowStyle = this.designSettings?.overflowStyle || "FULL_WIDTH";
            if (overflowStyle === "FADED") return "overflow-faded";
            if (overflowStyle === "CLIPPED") return "overflow-clipped";
            return "overflow-full-width";
        },
        baseSpeedPxPerSecond() {
            const speed = this.interactionSettings?.animationSpeed || "MEDIUM";
            const fontSize = Number(this.designSettings?.fontSettings?.size) || 36;
            const row = SPEED_CHART.reduce((best, cur) =>
                Math.abs(cur.textSize - fontSize) < Math.abs(best.textSize - fontSize) ? cur : best
            );
            return row[speed];
        },
        isClickable() {
            const clickType = this.interactionSettings?.clickInteraction || "NONE";
            if (clickType === "OPEN_LINK") {
                return !!this.interactionSettings?.clickUrl;
            }
            if (clickType === "NAVIGATE_TO_PAGE") {
                return !!this.interactionSettings?.clickPageId;
            }
            return false;
        },
        portfolio() {
            return this.viewData?.portfolio || this.$store.state.portfolio;
        },
        colorPalette() {
            return this.viewData?.portfolio?.designSettings?.colorPalette
                || this.$store.getters?.["design/colorPalette"]
                || this.$store.state.portfolio?.designSettings?.colorPalette
                || null;
        },
        isClipped() {
            return (this.designSettings?.overflowStyle || "FULL_WIDTH") === "CLIPPED";
        },
        clipBoundaryStyle() {
            const resolvedColor = this.resolveTextColor(this.designSettings?.clippedLineColor);
            return resolvedColor ? { background: resolvedColor } : null;
        },
    },
    methods: {
        resolveTextColor(colorStyle) {
            const colorData = colorStyle?.text?.idle;
            if (Number.isFinite(colorData?.paletteColorIndex)) {
                return this.colorPalette?.colors?.[colorData.paletteColorIndex]
                    || colorData?.color
                    || colorStyle?.primaryColor
                    || "#1D1D1D";
            }
            return colorData?.color || colorStyle?.primaryColor || "#1D1D1D";
        },
        tokenStyle() {
            return null;
        },
        resolveWaveHeight(fontSize) {
            const fallback = WAVE_CHART[1] || WAVE_CHART[0];
            const size = Number(fontSize) || fallback.textSize;
            const closest = WAVE_CHART.reduce((best, current) => {
                return Math.abs(current.textSize - size) < Math.abs(best.textSize - size) ? current : best;
            }, fallback);
            return Math.round(closest.waveHeight * this.resolveWaveViewportScale());
        },
        resolveWaveViewportScale() {
            const viewportWidth = window.innerWidth || 1440;
            if (viewportWidth <= 767) return 0.35;
            if (viewportWidth <= 1023) return 0.62;
            return 1;
        },
        resolveWaveCycles(fontSize) {
            const fallback = WAVE_CHART[1] || WAVE_CHART[0];
            const size = Number(fontSize) || fallback.textSize;
            const closest = WAVE_CHART.reduce((best, current) => {
                return Math.abs(current.textSize - size) < Math.abs(best.textSize - size) ? current : best;
            }, fallback);
            return closest.waveCycles || 1;
        },
        resolveDividerMetrics(fontSize) {
            const chart = this.isWavy ? WAVY_SIZE_CHART : LINEAR_SIZE_CHART;
            const fallback = chart[1] || chart[0];
            const size = Number(fontSize) || fallback.textSize;
            const closest = chart.reduce((best, current) => {
                return Math.abs(current.textSize - size) < Math.abs(best.textSize - size) ? current : best;
            }, fallback);
            return { dividerSize: closest.dividerSize, tokenGap: closest.tokenGap };
        },
        initializeTrackSetter() {
            if (!this.$refs.track) return;
            this.setTrackX = gsap.quickSetter(this.$refs.track, "x", "px");
            gsap.set(this.$refs.track, { x: 0, force3D: true });
        },
        applyTrackOffset() {
            if (!this.setTrackX) return;
            this.setTrackX(-this.marqueeOffsetPx);
        },
        refreshSequenceWidth() {
            const firstSequence = this.$refs.track?.querySelector(".animated-text-sequence");
            if (!firstSequence) return;
            // Use offsetWidth (layout size) — unaffected by CSS transform scale on ancestor.
            // This works even inside SectionPresetPreview where scale starts at 0.
            const gap = parseFloat(window.getComputedStyle(this.$refs.track).columnGap) || 0;
            const width = (firstSequence.offsetWidth || 0) + gap;
            if (!width) return;
            const cycleRepeatCount = this.resolveCycleRepeatCount(width);
            this.sequenceWidth = width;
            this.marqueeCycleWidth = width * cycleRepeatCount;
            this.marqueeOffsetPx = this.marqueeCycleWidth > 0
                ? this.marqueeOffsetPx % this.marqueeCycleWidth
                : 0;
            this.applyTrackOffset();
            const computedRepeatCount = Math.max(
                2,
                cycleRepeatCount + Math.ceil(window.innerWidth / this.sequenceWidth) + 2
            );
            this.repeatCount = computedRepeatCount;
            this.$nextTick(() => {
                if (this.isWavy) this.cacheTokenPositions();
                else this.resetWaveTransforms();
            });
        },
        cacheTokenPositions(retryCount = 0) {
            if (!this.$refs.track) return;
            const trackEl = this.$refs.track;
            // Derive the CSS ancestor scale: BCR reflects visual size, offsetWidth is natural size.
            // When SectionPresetPreview scale is 0 (initial render), BCR width is 0 — retry next frame.
            const cssScale = trackEl.getBoundingClientRect().width / (trackEl.offsetWidth || 1);
            if (cssScale <= 0) {
                if (retryCount < 10) requestAnimationFrame(() => this.cacheTokenPositions(retryCount + 1));
                return;
            }
            const trackRect = trackEl.getBoundingClientRect();
            const waveEls = Array.from(trackEl.querySelectorAll(".animated-text-char, .animated-text-token.divider-icon"));
            // Divide by cssScale to convert visual positions back to GSAP's natural coordinate space.
            this.waveTokenBaseX = waveEls.map((el) => (el.getBoundingClientRect().left - trackRect.left) / cssScale);
            this.waveTokenSetters = waveEls.map((el) => ({
                y: gsap.quickSetter(el, "y", "px"),
                rotation: gsap.quickSetter(el, "rotation", "deg"),
            }));
            const waveHeight = this.resolveWaveHeight(this.designSettings?.fontSettings?.size);
            const waveCycles = this.resolveWaveCycles(this.designSettings?.fontSettings?.size);
            this.waveAmplitudeCached = waveHeight / 2;
            this.waveKCached = this.marqueeCycleWidth > 0
                ? (2 * Math.PI * waveCycles) / this.marqueeCycleWidth
                : 0;
            this.applyWaveTransforms();
        },
        resolveCycleRepeatCount(sequenceWidth) {
            if (!this.isWavy) return 1;
            const safeWidth = Number(sequenceWidth) || 0;
            if (safeWidth <= 0) return 1;
            return Math.max(1, Math.ceil(window.innerWidth / safeWidth));
        },
        applyWaveTransforms() {
            if (!this.waveTokenSetters.length) return;
            const A = this.waveAmplitudeCached;
            const k = this.waveKCached;
            this.waveTokenSetters.forEach((setter, i) => {
                const x = this.waveTokenBaseX[i] - this.marqueeOffsetPx;
                const phase = k * x;
                setter.y(A * Math.sin(phase));
                setter.rotation(Math.atan(A * k * Math.cos(phase)) * (180 / Math.PI));
            });
        },
        resetWaveTransforms() {
            const els = this.$refs.track?.querySelectorAll(".animated-text-char, .animated-text-token.divider-icon");
            if (els) els.forEach((el) => gsap.set(el, { y: 0, rotation: 0 }));
            this.waveTokenSetters = [];
            this.waveTokenBaseX = [];
        },
        getCurrentSpeedPxPerSecond() {
            const baseSpeed = this.baseSpeedPxPerSecond;
            const hoverInteraction = this.interactionSettings?.hoverInteraction || "NONE";
            if (hoverInteraction === "PAUSE" && this.hoverActive) return 0;
            if (hoverInteraction === "SLOW_DOWN" && this.hoverActive) return baseSpeed * 0.45;
            return baseSpeed;
        },
        startMarqueeLoop() {
            if (this.gsapTickerCallback) return;
            let lastTime = gsap.ticker.time;
            this.gsapTickerCallback = () => {
                const now = gsap.ticker.time;
                const deltaSeconds = Math.max(0, now - lastTime);
                lastTime = now;
                const speed = this.getCurrentSpeedPxPerSecond();
                if (this.marqueeCycleWidth > 0 && speed > 0) {
                    this.marqueeOffsetPx = (this.marqueeOffsetPx + (speed * deltaSeconds)) % this.marqueeCycleWidth;
                    this.applyTrackOffset();
                }
                if (this.isWavy) this.applyWaveTransforms();
            };
            gsap.ticker.add(this.gsapTickerCallback);
        },
        stopMarqueeLoop() {
            if (!this.gsapTickerCallback) return;
            gsap.ticker.remove(this.gsapTickerCallback);
            this.gsapTickerCallback = null;
        },
        normalizeUrl(rawUrl) {
            const value = (rawUrl || "").trim();
            if (!value) return null;
            if (/^(https?:\/\/|mailto:|tel:)/i.test(value)) return value;
            return `https://${value}`;
        },
        rootIsOwnDomain() {
            return !!this.portfolio?.ownDomainAddress && location.origin.includes(this.portfolio.ownDomainAddress);
        },
        handleClick() {
            if (!this.isClickable) return;

            const clickType = this.interactionSettings?.clickInteraction || "NONE";
            if (clickType === "OPEN_LINK") {
                const url = this.normalizeUrl(this.interactionSettings?.clickUrl);
                if (url) {
                    window.open(url, "_blank");
                }
                return;
            }

            if (clickType !== "NAVIGATE_TO_PAGE") return;

            const pageId = this.interactionSettings?.clickPageId;
            const page = (this.$store.state.portfolioPages || []).find((item) => item?.shortId === pageId && !item?.deletedAt);
            if (!page) return;

            if (page.type === "home") {
                const homeRoute = this.rootIsOwnDomain() ? "/" : `/${this.portfolio?.shortId}`;
                this.$router.push(homeRoute);
                if (this.viewData) this.viewData.navOpen = false;
                return;
            }

            if (page.type === "file" || page.type === "link" || page.type === "mailto") {
                const targetUrl = page?.settings?.link?.url;
                if (!targetUrl) return;
                const prefix = page.type === "mailto" ? "mailto:" : "";
                const destination = `${prefix}${targetUrl}`;
                if (page?.settings?.link?.openInNewTab) {
                    window.open(destination, "_blank", "noopener,noreferrer");
                } else {
                    window.location.href = destination;
                }
                return;
            }

            const pageRoute = this.rootIsOwnDomain()
                ? `/p/${page.shortId}`
                : `/p/${this.portfolio?.shortId}/${page.shortId}`;
            this.$router.push(pageRoute);
            if (this.viewData) this.viewData.navOpen = false;
        },
    },
};
</script>

<style scoped lang="scss">
.animated-text-shell {
    grid-column: 1 / -1;
    position: relative;
    min-height: 56px;
    display: flex;
    align-items: center;
    overflow-x: clip;
    overflow-y: visible;
    user-select: none;

    &.overflow-full-width {
        width: 100vw;
        margin-left: calc(50% - 50vw);
    }

    &.overflow-clipped,
    &.overflow-faded {
        width: 100%;
    }

    &.overflow-faded {
        -webkit-mask-image: linear-gradient(to right, transparent 0%, #000 10%, #000 90%, transparent 100%);
        mask-image: linear-gradient(to right, transparent 0%, #000 10%, #000 90%, transparent 100%);
    }

    &.clickable {
        cursor: pointer;
    }

    &.layout-wavy {
        min-height: calc(var(--animated-text-line-height, 40px) + var(--animated-text-wave-height, 150px));
    }
}

.animated-text-track {
    display: flex;
    align-items: center;
    gap: var(--animated-text-sequence-gap, 36px);
    width: max-content;
    min-width: 100%;
    will-change: transform;
}

.clip-boundary {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 100%;
    background: transparent;
    opacity: 0.28;
    z-index: 2;
    pointer-events: none;

    &.left {
        left: 0;
    }

    &.right {
        right: 0;
    }
}

.animated-text-sequence {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    white-space: nowrap;
    gap: var(--animated-text-sequence-gap, 36px);
}

.animated-text-token {
    display: inline-block;
    white-space: nowrap;

    &.divider-icon {
        display: inline-flex;
        align-items: center;
        width: var(--animated-text-divider-size, 40px);
        height: var(--animated-text-divider-size, 40px);
        justify-content: center;
        line-height: 0;
        vertical-align: middle;

        :deep(svg) {
            width: 100%;
            height: 100%;
            display: block;
        }
    }
}

.animated-text-char {
    display: inline-block;
    white-space: pre;
}

</style>
